import React, { useEffect, useRef, useState } from 'react';
import { Badge, Button, Form, Modal } from 'react-bootstrap';
import { Icon } from '@lomarengas/design-system';
import { DeleteConfirmation } from '../../../common/components';
import { AccommodationImage } from './AccommodationImage';
import { AccommodationImageType } from './types';

interface AccommodationImagesProps {
  EditImage: (Id: string) => void;
  Images: AccommodationImageType[];
  RemoveImage: (Id: string) => void;
  HandleItemReorder: (OldNumber: number, NewNumber: number) => void;
  useGredi: boolean;
  saveImageList;
  showEdit: boolean;
  disabled?: boolean;
}

const AccommodationImages = ({
  EditImage,
  RemoveImage,
  Images,
  HandleItemReorder,
  useGredi,
  saveImageList,
  showEdit,
  disabled,
}: AccommodationImagesProps) => {
  const [imagesList, setImagesList] =
    useState<AccommodationImageType[]>(Images);
  const [imageViewModalNumber, setImageViewModalNumber] = useState<number>(0);
  const [showImageViewModal, setShowImageViewModal] = useState<boolean>(false);
  const [imageDragged, setImageDragged] = useState<number | null>(null);
  const [activeDropzone, setActiveDropzone] = useState<number | null>(null);

  const [imageEditModalItem, setImageEditModalItem] =
    useState<AccommodationImageType>(null);
  const [showImageEditModal, setShowImageEditModal] = useState<boolean>(false);

  const [imageToBeRemovedId, setImageToBeRemovedId] = useState<string>('');
  const [isDeleteConfirmationVisible, setIsDeleteConfirmationVisible] =
    useState<boolean>(false);

  const HandleImageClick = (
    ImageNumber: number,
    _ImageSrc: string,
    _ImageDescription: string,
  ) => {
    setImageViewModalNumber(ImageNumber);
    // Defer opening the modal to the next frame to avoid ResizeObserver warning during layout measurements
    if (typeof window !== 'undefined' && 'requestAnimationFrame' in window) {
      window.requestAnimationFrame(() => setShowImageViewModal(true));
    } else {
      setShowImageViewModal(true);
    }
  };

  const HandleImageArrowClick = (ImageNumber: number) => {
    setImageViewModalNumber(ImageNumber);
  };

  const editImages = (
    imagesIn: AccommodationImageType[],
    valuedata: AccommodationImageType,
  ) => {
    if (valuedata.id !== 'new') {
      return imagesIn.map(item => {
        if (item.id === valuedata.id) {
          item = valuedata;
        }
        return item;
      });
    }
    setImageEditModalItem(valuedata);
    imagesIn = [...imagesIn, valuedata];
    return imagesIn;
  };

  const handleInputChangeImage = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    setImageEditModalItem({
      ...imageEditModalItem,
      [name]: value,
    });
    const editedImageTemp = {
      ...imageEditModalItem,
      [name]: value,
    };
    const images = editImages(imagesList, editedImageTemp);
    setImagesList(images);
  };

  const handleSaveImages = event => {
    setShowImageEditModal(false);
    saveImageList(imagesList);
  };

  const HandleImageEditClick = (Id: string) => {
    const ImageItem = imagesList.filter(obj => {
      return obj.id === Id;
    });

    EditImage(Id);
    setImageEditModalItem(ImageItem[0]);
    setShowImageEditModal(true);
  };

  const HandleImageRemoveClick = (Id: string) => {
    setImageToBeRemovedId(Id);
    setIsDeleteConfirmationVisible(true);
  };

  useEffect(() => {
    if (Images && Images.length > 0) {
      setImagesList(Images);
    }
  }, [Images]);

  const HandleImageRemove = (Id: string) => {
    RemoveImage(imageToBeRemovedId);
    setIsDeleteConfirmationVisible(false);
  };
  if (imagesList.length < 1) {
    return <p>Ei kuvia.</p>;
  }

  const HandleDragOver = (Index: number) => {
    if (Index !== imageDragged) {
      setActiveDropzone(Index);
    } else {
      setActiveDropzone(null);
    }
  };

  const HandleImageDropped = (DroppedLocation: number) => {
    if (DroppedLocation !== imageDragged) {
      HandleItemReorder(imageDragged, DroppedLocation);
    }
  };

  const HandleImageDragEnd = () => {
    setImageDragged(null);
    setActiveDropzone(null);
  };

  // Safely derive the currently selected image for the modal
  const currentImage =
    imageViewModalNumber > 0 && imageViewModalNumber <= imagesList.length
      ? imagesList[imageViewModalNumber - 1]
      : undefined;

  return (
    <>
      {imagesList.length > 0 ? (
        <div className="row row-cols-1 row-cols-md-2 row-cols-xl-4 g-16">
          {imagesList.map((item, index) => (
            <div
              key={item.id}
              className={`col drag-draggable rounded${
                imageDragged === item.index ? ' drag-draggable-active' : ''
              }${activeDropzone === item.index ? ' drag-dropzone' : ''}`}
              onDragEnd={() => HandleImageDragEnd()}
              onDragOver={e => HandleDragOver(item.index)}
              onDragStart={() => setImageDragged(item.index)}
              onDrop={() => HandleImageDropped(item.index)}
              draggable="true"
            >
              <AccommodationImage
                HandleImageEdit={HandleImageEditClick}
                HandleImageRemove={HandleImageRemoveClick}
                HandleImageClick={HandleImageClick}
                HandleItemReorder={HandleItemReorder}
                ImageId={item.id}
                ImageNumber={item.index}
                ImageSrc={item.fullUrl}
                ImageText={`${item.fileName.toString()}`}
                ImageDescription={`${item.description ? item.description : ''}`}
                TotalImages={Images.length}
                showEdit={showEdit}
                showDelete={!useGredi}
                disabled={disabled}
              />
            </div>
          ))}
        </div>
      ) : (
        <>Ei Kuvia</>
      )}

      <Modal
        size="xl"
        show={showImageViewModal}
        onHide={() => setShowImageViewModal(false)}
      >
        <Modal.Header closeButton className="bg-white">
          <Button
            className="me-8"
            disabled={disabled || imageViewModalNumber === 1}
            onClick={() => HandleImageArrowClick(imageViewModalNumber - 1)}
            size="sm"
            title="Edellinen"
            variant="outline-blue"
          >
            <Icon Name="arrow-left" />
          </Button>
          <Button
            className="me-8"
            disabled={disabled || imageViewModalNumber === imagesList.length}
            onClick={() => HandleImageArrowClick(imageViewModalNumber + 1)}
            size="sm"
            title="Seuraava"
            variant="outline-blue"
          >
            <Icon Name="arrow-right" />
          </Button>
          <Badge bg="white" className="fs-6">
            {imageViewModalNumber} / {imagesList.length}
          </Badge>
        </Modal.Header>
        <Modal.Body>
          <div>{currentImage?.description ?? ''}</div>
          {currentImage?.fullUrl && (
            <img
              src={currentImage.fullUrl}
              alt={currentImage.altText || currentImage.fileName || ''}
              className="d-block mx-auto mw-100"
              style={{ maxHeight: '80vh' }}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showImageEditModal}
        onHide={() => setShowImageEditModal(false)}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Muokkaa kuvan tietoja</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="editModalDemo1">
            <Form.Label>Kuvaus</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              onChange={handleInputChangeImage}
              disabled={useGredi}
              defaultValue={
                imageEditModalItem ? imageEditModalItem.description : ''
              }
            />
          </Form.Group>
          <hr className="hr-light my-32" />
          <Form.Group controlId="editModalDemo2">
            <Form.Label>Kuvaus En</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="descriptionEn"
              disabled={useGredi}
              onChange={handleInputChangeImage}
              defaultValue={
                imageEditModalItem ? imageEditModalItem.descriptionEn : ''
              }
            />
          </Form.Group>
          <hr className="hr-light my-32" />
          <Form.Group controlId="editModalDemo3">
            <Form.Label>Kuvaus Ru</Form.Label>
            <Form.Control
              as="textarea"
              rows={1}
              name="descriptionRu"
              disabled={useGredi}
              onChange={handleInputChangeImage}
              defaultValue={
                imageEditModalItem ? imageEditModalItem.descriptionRu : ''
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => handleSaveImages(imagesList)}
          >
            Tallenna
          </Button>
        </Modal.Footer>
      </Modal>

      <DeleteConfirmation
        ConfirmDelete={() => HandleImageRemove(imageToBeRemovedId)}
        HideModal={() => setIsDeleteConfirmationVisible(false)}
        Id={imageToBeRemovedId}
        Message="Haluatko varmasti poistaa kohteen kuvan?"
        ShowModal={isDeleteConfirmationVisible}
      />
    </>
  );
};

export default AccommodationImages;
export { AccommodationImages };
export type { AccommodationImagesProps };
